/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require("./bootstrap");

import devtools from "@vue/devtools";
if (process.env.NODE_ENV === "development") {
    devtools.connect("localhost", "3000");
}

window.Vue = require("vue");

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */

import GoLotusHeader from "./components/GoLotusHeader";
import GoLotusFooter from "./components/GoLotusFooter";
import TimeoutDialog from "./components/TimeoutDialog";

import AcceptInvite from "./components/AcceptInvite";
import Account from "./components/Account";
import Billing from "./components/Billing";
import Goals from "./components/Goals.vue";
import Schedule from "./components/Schedule";
import TrackerSession from "./components/TrackerSession";
import CustomizeTrackers from "./components/CustomizeTrackers";
import PatientProgress from "./components/PatientProgress";
import PatientDetails from "./components/PatientDetails";
import Patients from "./components/Patients";
import TreatmentPlan from "./components/TreatmentPlan";
import Walkthrough from "./components/Walkthrough";
import Connections from "./components/Connections";
import ControlCenter from "./components/ControlCenter";
import AccountSettings from "./components/AccountSettings";
import HelpCenter from "./components/HelpCenter";
import TermsConditions from "./components/TermsConditions";
import SideMenuWrapper from "./components/ui/SideMenuWrapper";
import TherapistDetails from "./components/TherapistDetails";
import Therapists from "./components/Therapists";
import Upgrade from "./components/Upgrade";
import UpgradeWidget from "./components/widgets/UpgradeWidget";
import FailedChargeWidget from "./components/widgets/FailedChargeWidget";
import OnboardingChecklist from "./components/widgets/OnboardingChecklist";
import FundingSources from "./components/FundingSources";
import Reports from "./components/Reports";

import IdleVue from "idle-vue";
import { parentChildCommunication } from "./plugins/parentChildCommunication";

Vue.mixin(parentChildCommunication);

import VTooltip from "v-tooltip";
Vue.use(VTooltip);

import moment from "moment";
Vue.prototype.moment = moment;

import VueTheMask from "vue-the-mask";
Vue.use(VueTheMask);

import ToggleButton from "vue-js-toggle-button";
Vue.use(ToggleButton);

import ProgressBar from "progressbar.js";
Vue.prototype.ProgressBar = ProgressBar;

import Vue from "vue";
import VCalendar from "v-calendar";
import "v-calendar/lib/v-calendar.min.css";
Vue.use(VCalendar, {
    firstDayOfWeek: 2, // Monday
    datePickerTintColor: "#D64571",
});

Vue.prototype.$bus = new Vue(); // Global event bus

Vue.use(IdleVue, {
    eventEmitter: Vue.prototype.$bus,
    // Timeout is in milliseconds, currently set to 15 minutes
    idleTime: process.env.MIX_SESSION_CLIENT_TIMEOUT,
});

Vue.prototype.$timeout = process.env.MIX_SESSION_CLIENT_TIMEOUT;

if (window.env) {
    // Global variable to represent environment
    Vue.prototype.$env = window.env;
}

var Rollbar = require("vue-rollbar");

Vue.use(Rollbar, {
    accessToken: "05ca8af1829a4a4cbf84ba48b43a64cd",
    captureUncaught: true,
    captureUnhandledRejections: true,
    enabled: true,
    source_map_enabled: true,
    environment: Vue.prototype.$env,
    payload: {
        client: {
            javascript: {
                source_map_enabled: true,
                code_version: process.env.RELEASE_TAG,
                guess_uncaught_frames: true,
            },
        },
    },
});

import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
Vue.use(VueSweetalert2);

import VwMultiSelect from "@vectorwyse/vw-multi-select";
Vue.component("vw-multi-select", VwMultiSelect);

Vue.config.errorHandler = function(err, vm, info) {
    if (["production", "demo"].includes(window.env)) {
        Vue.rollbar.error(err);
    } else {
        console.error(err);
    }
};

import { helpers } from "./plugins/helpers";
Vue.mixin(helpers);
Vue.mixin({
    data() {
        return {
            helpers,
        };
    },
});

new Vue({
    el: "#app",
    components: {
        GoLotusHeader,
        GoLotusFooter,
        TimeoutDialog,
        AcceptInvite,
        Account,
        Billing,
        Goals,
        Schedule,
        TrackerSession,
        PatientProgress,
        PatientDetails,
        Patients,
        TreatmentPlan,
        CustomizeTrackers,
        Walkthrough,
        Connections,
        ControlCenter,
        HelpCenter,
        AccountSettings,
        TermsConditions,
        SideMenuWrapper,
        TherapistDetails,
        Therapists,
        UpgradeWidget,
        FailedChargeWidget,
        Upgrade,
        OnboardingChecklist,
        FundingSources,
        Reports,
    },
    data: {
        appUrl: "",
        hideNavigation: false,
        currentPage: "My Account",
        isCollapsed: false,
        user: {},
        organization: {},
        organizations: [],
        permissions: [],
        userClients: [],
        userOrgClients: [],
        helpCategories: [],
        subscription: [],
    },
    created() {
        this.getCurrentPage();
        this.$bus.$on("refresh-clients", () => {
            this.fetchUserClients();
            this.fetchOrganizationClients();
        });
    },
    mounted() {
        this.parseAttributesData();
    },
    computed: {
        organizationType() {
            if (this.organization) {
                return this.organization.type;
            }
            return null;
        },
    },
    methods: {
        parseAttributesData() {
            const attributes = [
                "appUrl",
                "user",
                "organization",
                "organizations",
                "permissions",
                "userOrgClients",
                "subscription",
                "helpCategories",
            ];
            attributes.forEach(attribute => {
                if (this.$el.attributes[attribute]) {
                    try {
                        this[attribute] = JSON.parse(this.$el.attributes[attribute].value);
                    } catch (e) {
                        this[attribute] = this.$el.attributes[attribute].value;
                    }
                }
                this.$el.removeAttribute(attribute);
            });
            this.populateSideMenu();
        },
        populateSideMenu() {
            this.populateSideMenuClients(this.userOrgClients);
            switch (this.organizationType) {
                case "professional_gold":
                    this.populateSideMenuGold();
                    this.populateSideMenuControlCenter();
                    break;
                case "professional_platinum":
                    this.populateSideMenuPlatinum();
                    this.populateSideMenuControlCenter();
                    break;
                default:
                    if (this.$env === "development") {
                        //this.populateSideMenuSuperUser();
                    }
            }
            this.populateSideMenuHelp();
        },
        populateSideMenuClients(userOrganizationClients) {
            const allPatientsWithGoals = this.$el.attributes.patientsGoals
                ? JSON.parse(this.$el.attributes.patientsGoals.value)
                : [];
            this.$el.removeAttribute("patientsGoals");
            userOrganizationClients.forEach(
                u => (u.goals = allPatientsWithGoals.find(p => p.id === u.id).goals)
            ); //Adds goals to the patient list used by the side menu
            this.$bus.$emit("populate-sidemenu-clients", userOrganizationClients);
        },
        populateSideMenuControlCenter() {
            if (
                [
                    "ccenter.centers.view",
                    "ccenter.permissions.view",
                    "ccenter.accounts.view",
                    "ccenter.funding.view",
                    //'ccenter.appointments.view'
                ].some(p => this.permissions.includes(p))
            ) {
                this.$bus.$emit("populate-sidemenu-control-center"); // Adds control center to side menu if user can view any of the sub components
            }
        },
        populateSideMenuGold() {
            this.$bus.$emit("populate-sidemenu-gold");
        },
        populateSideMenuPlatinum() {
            this.$bus.$emit("populate-sidemenu-platinum");
        },
        populateSideMenuSuperUser() {
            if (this.user && this.user.is_super_user === 1) {
                this.$bus.$emit("populate-sidemenu-super-user");
            }
        },
        populateSideMenuHelp() {
            let helpCategoriesItems = this.helpCategories;
            helpCategoriesItems.forEach(h => {
                h.title = h.name;
                h.url = `/help/category/${h.id}`;
            });
            if (
                helpCategoriesItems.length &&
                helpCategoriesItems.some(c => c.help_article.length)
            ) {
                this.$bus.$emit("populate-sidemenu-help", helpCategoriesItems);
            }
        },
        fetchUserClients() {
            axios.get("/api/patients").then(r => {
                this.userClients = r.data.data;
            });
        },
        fetchOrganizationClients() {
            let params = { params: { organization_id: this.organization.id } };
            axios.get("/api/patients", params).then(r => {
                this.userOrgClients = r.data.data;
                this.populateSideMenuClients(JSON.parse(JSON.stringify(r.data.data)));
            });
        },
        getCurrentPage() {
            if (window.location.pathname === "/") {
                this.currentPage = "My Account";
            }
            if (window.location.pathname === "/account") {
                this.currentPage = "My Account";
            }
            if (window.location.pathname.split("/").slice(-1)[0] === "goals") {
                this.currentPage = "Goals";
            }
            if (window.location.pathname.split("/").slice(-2)[0] === "patients") {
                this.currentPage = "Patient Overview";
            }
            if (window.location.pathname.split("/").slice(-2)[0] === "treatment-plan") {
                this.currentPage = "Treatment Plan";
            }
            if (window.location.pathname.split("/").slice(-1)[0] === "data-tracker") {
                this.currentPage = "Data Tracker";
            }
        },
        setOrganization(id = 0) {
            axios.get(`/api/organizations/${id}/set-active`).then(() => {
                window.location.href = "/account";
            });
        },
    },
});
